<template>
  <v-menu :nudge-bottom="10"
          :nudge-left="nudgeLeft"
          :offset-y="true"
          :disabled="disabled"
          v-model="menuVal"
          :content-class="cssClass"
          :attach="attach"
          bottom>
    <template #activator="{ on }">
      <!-- extra div needed so it's displayed correctly at fin-header-footer-container in nested cols -->
      <div style="display: inline-block;">
        <v-btn v-on="on"
               :class="['dropdown-btn pa-0 mr-4', {disabled}]"
               elevation="0"
               text>
        <span class="dropdown-label">
          <span :class="{ bold: !showSelection || selection.length === 0 }">
            {{ label + (singleSelect && showSelection && selection.length === 1 ? ":" : "") }}
          </span>
          <span v-if="singleSelect && showSelection && selection.length === 1"
                class="mx-2 bold">
            <!-- @slot Displays the title of the selected item at single select -->
            <slot :items="value" name="selection">
              <span>{{ selection[0][outputLabelAttribute] }}</span>
            </slot>
          </span>
        </span>

          <v-badge v-if="!singleSelect && showSelectionBadge && selection.length > 0"
                   :content="selection.length"
                   class="selectionBadge ml-2 mr-2 mt-n3"
                   offset-y="8px"/>

          <v-icon class="mx-2">icon-arrow-down</v-icon>
        </v-btn>
      </div>
    </template>

    <v-list v-resize="updateScrollbar"
            :style="{maxHeight: dropdownHeight}">
      <v-list-item v-if="!singleSelect && !hideAllSelection"
                   style="background-color: #fff;"
                   @click.stop.prevent="() => false">
        <v-checkbox v-model="allSelected"
                    :indeterminate="indeterminate"
                    class="selectAll mr-3 pa-0"
                    hide-details
                    on-icon="icon-check">
          <template #label>Alle</template>
        </v-checkbox>
      </v-list-item>

      <v-divider v-if="!singleSelect && !hideAllSelection"/>

      <template v-if="!singleSelect">
        <v-list-item v-for="(item, index) in items"
                     :key="index"
                     style="background-color: #fff;"
                     @click.stop.prevent="() => false">
          <v-checkbox :input-value="selection.find(v => v[checkAttribute] === item[checkAttribute])"
                      class="mr-3 pa-0"
                      hide-details
                      on-icon="icon-check"
                      @change="val => check(item, val)">
            <template #label :item="item">
              <!-- @slot Displays the item's configured attribute as the checkbox label for multi selects and the list item title for single selects -->
              <slot :item="item">{{ item[outputLabelAttribute] }}</slot>
            </template>
          </v-checkbox>
        </v-list-item>
      </template>

      <template v-else>
        <v-list-item v-for="(item, index) in items"
                     :key="index"
                     style="background-color: #fff;"
                     @click="changeSingleSelection(item)">
          <v-list-item-icon :class="`${$vuetify.breakpoint.xs ? 'mr-0' : 'mr-2'} my-3`">
            <v-icon small v-if="selection.find(s => s[checkAttribute] === item[checkAttribute])"
                    :color="$finColors.primaryDark" class="selectedItem">
              icon-check
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <!-- @slot Displays the item's configured attribute as the list item title for single selects -->
              <slot :item="item">{{ item[outputLabelAttribute] }}</slot>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
import perfectscrollbar from "fincraft-lib-basics/src/mixins/perfectscrollbar"

export default {
  name: "FinDropdown",
  components: {},
  mixins: [perfectscrollbar],
  props: {
    /** The dropdown label */
    label: {
      type: String,
      default: ""
    },
    /** All selectable items */
    items: {
      type: Array,
      required: true
    },
    /** The selected items */
    value: {
      type: Array,
      default: () => []
    },
    /** Allow only single select, default: multi select */
    singleSelect: {
      type: Boolean,
      default: false
    },
    /** Hide the option to select all items at once, only usable with singleSelect set to false */
    hideAllSelection: {
      type: Boolean,
      default: false
    },
    /** Displays the first selected item at the label, only usable with singleSelect set to true */
    showSelection: {
      type: Boolean,
      default: false
    },
    /** Show the nr of selected items as badge, only usable with singleSelect set to false */
    showSelectionBadge: {
      type: Boolean,
      default: false
    },
    /** Nudge the content to the left */
    nudgeLeft: {
      type: Number,
      default: 0
    },
    /** The item attribute to check for selected items, see @check method */
    checkAttribute: {
      type: String,
      default: 'id'
    },
    /** The attribute of the item to display */
    outputLabelAttribute: {
      type: String,
      default: 'title'
    },
    /** Height of the dropdown */
    dropdownHeight: {
      type: String,
      default: '50vh'
    },
    /** Disables the v-menu dropdown */
    disabled: {
      type: Boolean,
      default: false
    },
    /** Attaches the v-menu to a dom element specified by the given query selector, useful e.g. at dialogs */
    attach: {},
  },
  data() {
    return {
      selection: this.value,
      menuVal: false,
      cssClass: 'findropdown_' + Math.random().toString(36).substr(2, 9)
    }
  },
  computed: {
    allSelected: {
      get() {
        return this.selection.length === this.items?.length
      },
      set(val) {
        this.selection = val ? [...this.items] : []
        this.$emit('input', this.selection)
      }
    },
    indeterminate() {
      return !this.allSelected && this.selection.length > 0
    }
  },
  methods: {
    check(item, val) {
      const clone = [...this.selection]
      // hint: cannot use clone.indexOf(item) as the objects aren't equal => use findIndex
      const index = clone.findIndex(c => c[this.checkAttribute] === item[this.checkAttribute])

      // checkbox === true && item not in current selection => add
      if (val && index === -1) {
        clone.push(item)
      }
      // checkbox === false && item in current selection => remove
      else if (!val && index > -1) {
        clone.splice(index, 1)
      }

      this.selection = clone

      /**
       * Emits the selected items on change
       */
      this.$emit('input', clone)
    },
    changeSingleSelection(item) {
      if (this.selection.find(s => s[this.checkAttribute] === item[this.checkAttribute])) {
        this.selection = []
      } else {
        this.selection = [item]
      }

      this.$emit('input', this.selection)
    },
    getScrollElement() {
      return document.querySelector('.' + this.cssClass + '.v-menu__content')
    },
  },
  watch: {
    value(val) {
      this.selection = val
    },
    menuVal(val) {
      if (val) {
        this.$nextTick(() => {
          this.updateScrollbar()
        })
      } else {
        this.detachScrollbar()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dropdown-btn {
  max-width: inherit;
  justify-content: start;
  overflow: hidden;
  text-align: left;
}

::v-deep .v-btn__content {
  max-width: inherit;
}

.dropdown-btn.disabled {
  opacity: 0.4;
  cursor: default;
}

.dropdown-label {
  // 16px instead of 14px because of iOS zooming for element smaller than 16px
  font-weight: normal;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
  letter-spacing: normal;

  max-width: inherit;
  text-overflow: ellipsis;
  overflow: hidden;

  .bold {
    font-weight: 600;
  }
}

.v-input--selection-controls {
  /* delete the default mt of 16px to vertical center contents */
  margin-top: 0;
  padding: 0 5px;
}
</style>
